import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import { CheckCircle2, CalendarClock, ArrowLeft } from "lucide-react";
import { toast, Toaster } from "sonner";
import { formatDate } from "../lib/format";

export default function FollowUps() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/candidates/follow-ups/mine");
      setItems(data.items);
      setTotal(data.total);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resolve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/candidates/${id}/resolve-followup`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      setTotal((n) => n - 1);
      toast.success("Follow-up resolved");
    } catch {
      toast.error("Could not resolve follow-up");
    } finally { setBusy(null); }
  };

  const overdue = items.filter((c) => c.follow_up_overdue_days > 0);
  const today = items.filter((c) => c.follow_up_overdue_days === 0);
  const upcoming = items.filter((c) => c.follow_up_overdue_days < 0);

  return (
    <Layout>
      <Toaster richColors position="top-right" />
      <Link to="/" className="text-sm text-slate-500 hover:text-slate-900 mb-4 inline-flex items-center gap-1" data-testid="followups-back">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>
      <div className="mb-6 flex items-center gap-3">
        <CalendarClock className="w-6 h-6 text-indigo-600" />
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="followups-title">All follow-ups</h1>
          <p className="text-slate-500 mt-1">{total} candidate{total === 1 ? "" : "s"} scheduled. Most overdue first.</p>
        </div>
      </div>

      {loading ? <div className="text-slate-500">Loading…</div> : (
        <div className="space-y-6">
          <Section title="Overdue" tone="red" list={overdue} busy={busy} resolve={resolve} testId="section-overdue" />
          <Section title="Due today" tone="amber" list={today} busy={busy} resolve={resolve} testId="section-today" />
          <Section title="Upcoming" tone="emerald" list={upcoming} busy={busy} resolve={resolve} testId="section-upcoming" />

          {items.length === 0 && (
            <Card className="p-12 text-center border-slate-200">
              <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
              <p className="text-slate-500">You&apos;re all caught up. Nothing to follow up on right now.</p>
            </Card>
          )}
        </div>
      )}
    </Layout>
  );
}

function Section({ title, tone, list, busy, resolve, testId }) {
  if (list.length === 0) return null;
  const dot = { red: "bg-red-500", amber: "bg-amber-500", emerald: "bg-emerald-500" }[tone];
  return (
    <div data-testid={testId}>
      <h3 className="font-display font-semibold text-lg text-slate-900 mb-3 flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} /> {title} <span className="text-slate-400 text-sm font-normal">({list.length})</span>
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {list.map((c) => (
          <Card key={c.id} className="p-4 border-slate-200 hover:border-indigo-300 transition-colors" data-testid={`followup-item-${c.id}`}>
            <Link to={`/candidate/${c.id}`} className="block">
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{c.candidate_name}</div>
                  <div className="text-xs text-slate-500 truncate">{(Array.isArray(c.domain) ? c.domain.join(", ") : c.domain) || "—"} · {c.current_city || "—"}</div>
                </div>
                <StatusBadge status={c.candidate_status} />
              </div>
              <div className="text-xs mt-1">
                <span className="text-slate-600">Due {formatDate(c.next_follow_up)}</span>
                {c.follow_up_overdue_days > 0 && <span className="text-red-600 italic ml-2">Overdue by {c.follow_up_overdue_days} day{c.follow_up_overdue_days === 1 ? "" : "s"}</span>}
                {c.follow_up_overdue_days === 0 && <span className="text-amber-600 italic ml-2">Today</span>}
                {c.follow_up_overdue_days < 0 && <span className="text-emerald-600 italic ml-2">In {-c.follow_up_overdue_days} day{c.follow_up_overdue_days === -1 ? "" : "s"}</span>}
              </div>
              {c.follow_up_set_by && (
                <div className="text-xs text-slate-400 mt-1">Set by {c.follow_up_set_by}</div>
              )}
            </Link>
            <div className="mt-2 pt-2 border-t border-slate-100 flex justify-between">
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-emerald-700 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid={`resolve-followup-${c.id}`}
                  disabled={busy === c.id}
                  onChange={() => resolve(c.id)}
                  className="w-3.5 h-3.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                Resolve
              </label>
              <Link to={`/candidate/${c.id}`} className="text-xs text-indigo-600 hover:text-indigo-700">Open →</Link>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
